/**
 * 🐋 Push Notification Form - Cetalingua FM
 * Target path: /src/components/advertiser/PushNotificationForm.js
 * REPLACES existing PushNotificationForm.js
 * 
 * CHANGES FROM ORIGINAL:
 * - Monthly limit → Weekly limit (paid tier)
 * - Writes to 'pushNotifications' collection (not 'pushNotificationRequests')
 * - Adds content selection: choose which approved content to include in push
 * - Uses publishPeriod (ISO week) for cadence enforcement
 * - Species displayed as names (from numeric IDs)
 * OCT 2026: ONE push per CALENDAR MONTH (plans say "1 push notification per month"; was weekly). A rejected push
 *   does not use up the month — the advertiser can fix it and send again. The month is counted from the pushes
 *   themselves (not from users.lastPushPeriod, which blocked the month even after a rejection).
 */

import React, { useState, useEffect } from 'react';
import {
  doc, getDoc, addDoc, collection, query, where, getDocs,
  Timestamp, orderBy, limit
} from 'firebase/firestore';
import { db } from '../../firebase-config';
import { getNextPeriodDate, getMonthPeriod, LIMITS } from '../../shared/advertiserConstants';
import { getSpeciesSummary } from '../../shared/speciesUtils';
import './PushNotificationForm.css';
import SpeciesReach from './SpeciesReach';

const PushNotificationForm = ({ userData, userId }) => {
  const [formData, setFormData] = useState({
    title: '',
    body: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [canSend, setCanSend] = useState(true);
  const [lastSentDate, setLastSentDate] = useState(null);
  const [pendingNotification, setPendingNotification] = useState(null);
  const [lastRejected, setLastRejected] = useState(null);   // latest rejected push this month (title, body, reason)

  // Approved content for selection
  const [approvedContent, setApprovedContent] = useState([]);
  const [selectedContentIds, setSelectedContentIds] = useState([]);

  const currentPeriod = getMonthPeriod(); // one push per calendar month, e.g. "2026-10"
  const nextPeriodDate = getNextPeriodDate('monthly');

  // Check if user can send notification this week + fetch approved content
  useEffect(() => {
    checkNotificationStatus();
    fetchApprovedContent();
  }, [userId]);

  const checkNotificationStatus = async () => {
    try {
      // Push already submitted / sent this month (a rejected one does not count)
      const pushQuery = query(
        collection(db, 'pushNotifications'),
        where('advertiserId', '==', userId),
        where('publishPeriod', '==', currentPeriod)
      );
      const pushSnap = await getDocs(pushQuery);
      const counting = pushSnap.docs.map((d) => d.data()).filter((p) => p.status !== 'rejected');
      // the latest REJECTED push this month: shown with the admin's reason (user, 9 Oct 2026: the advertiser could
      // send again but was never told that, or why, the push was rejected)
      const ts = (v) => (v?.toMillis ? v.toMillis() : 0);
      const rej = pushSnap.docs.map((d) => d.data()).filter((p) => p.status === 'rejected')
        .sort((a, b) => ts(b.rejectedAt || b.createdAt) - ts(a.rejectedAt || a.createdAt))[0];
      setLastRejected(rej ? { title: rej.pushTitle, body: rej.pushBody, reason: rej.rejectionReason || '',
        at: rej.rejectedAt?.toDate ? rej.rejectedAt.toDate() : null, selectedContent: rej.selectedContent || [] } : null);

      if (counting.length) {
        const existing = counting[0];
        if (existing.status === 'pending') {
          setPendingNotification({
            title: existing.pushTitle,
            body: existing.pushBody,
            status: 'pending',
            selectedContent: existing.selectedContent || [],
          });
        }
        setCanSend(false);
        if (existing.sentAt) {
          setLastSentDate(existing.sentAt.toDate());
        }
        return;
      }

      // Also check legacy pushNotificationRequests
      const legacyQuery = query(
        collection(db, 'pushNotificationRequests'),
        where('advertiserId', '==', userId),
        where('status', '==', 'pending')
      );
      const legacySnap = await getDocs(legacyQuery);
      if (!legacySnap.empty) {
        const legacy = legacySnap.docs[0].data();
        setPendingNotification({
          title: legacy.title,
          body: legacy.body,
          status: 'pending',
          selectedContent: [],
        });
      }

      // Last push actually SENT (the server also allows only one per month)
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const last = data.lastPushSentAt?.toDate ? data.lastPushSentAt.toDate() : null;
        const now = new Date();
        if (last && last.getUTCFullYear() === now.getUTCFullYear() && last.getUTCMonth() === now.getUTCMonth()) {
          setCanSend(false);
        }
        if (data.lastPushSentAt) {
          setLastSentDate(data.lastPushSentAt.toDate());
        }
      }
    } catch (error) {
      console.error('Error checking notification status:', error);
    }
  };

  const fetchApprovedContent = async () => {
    try {
      const q = query(
        collection(db, 'advertiserContent'),
        where('advertiserId', '==', userId),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const snap = await getDocs(q);
      setApprovedContent(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error fetching approved content:', error);
    }
  };

  const toggleContentSelection = (contentId) => {
    setSelectedContentIds(prev =>
      prev.includes(contentId)
        ? prev.filter(id => id !== contentId)
        : [...prev, contentId]
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }
    if (formData.title.length > LIMITS.PUSH_TITLE_MAX) {
      setMessage({ type: 'error', text: `Title must be ${LIMITS.PUSH_TITLE_MAX} characters or less` });
      return;
    }
    if (!formData.body.trim()) {
      setMessage({ type: 'error', text: 'Message is required' });
      return;
    }
    if (formData.body.length > LIMITS.PUSH_BODY_MAX) {
      setMessage({ type: 'error', text: `Message must be ${LIMITS.PUSH_BODY_MAX} characters or less` });
      return;
    }

    setLoading(true);

    try {
      // Build selected content array for push doc
      const selectedContent = selectedContentIds.map(id => {
        const item = approvedContent.find(c => c.id === id);
        return item ? { contentId: id, type: item.type, title: item.title } : null;
      }).filter(Boolean);

      // Create notification request in new collection
      const notificationDoc = {
        advertiserId: userId,
        advertiserName: userData.companyName || 'Unknown',
        advertiserEmail: userData.email,
        selectedContent: selectedContent,
        pushTitle: formData.title.trim(),
        pushBody: formData.body.trim(),
        targetSpecies: userData.species || [],
        status: 'pending',
        rejectionReason: null,
        publishPeriod: currentPeriod,
        sentAt: null,
        recipientCount: null,
        failedCount: null,
        sending: false,
        createdAt: Timestamp.now(),
        approvedAt: null,
        approvedBy: null,
        rejectedAt: null,
        rejectedBy: null,
      };

      await addDoc(collection(db, 'pushNotifications'), notificationDoc);
      setLastRejected(null);

      setPendingNotification({
        title: formData.title.trim(),
        body: formData.body.trim(),
        status: 'pending',
        selectedContent: selectedContent,
      });
      setCanSend(false);

      setFormData({ title: '', body: '' });
      setSelectedContentIds([]);
      setMessage({
        type: 'success',
        text: 'Push notification submitted for admin approval!'
      });

    } catch (error) {
      console.error('Error submitting notification:', error);
      setMessage({ type: 'error', text: 'Failed to submit notification. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Countdown to the 1st of next month
  const getNextAvailableText = () => {
    const now = new Date();
    const diff = nextPeriodDate - now;
    if (diff <= 0) return 'Available now!';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} and ${hours} hour${hours !== 1 ? 's' : ''}`;
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  };

  const contentTypeEmojis = {
    featured_media: '📸',
    whats_new: '📰',
    hot_deal: '🔥',
  };

  return (
    <div className="push-notification-form">
      <div className="section-header">
        <h2>📱 Push Notifications</h2>
        <p>Send a monthly notification to Cetalingua FM users interested in your whale species</p>
      </div>

      {/* Feature Info */}
      <div className="feature-info">
        <div className="info-item">
          <span className="info-icon">🎯</span>
          <div>
            <h4>Targeted Reach</h4>
            <p>Your notification reaches users interested in: <strong>{getSpeciesSummary(userData?.species || [], 3)}</strong></p>
            <SpeciesReach variant="line" />
          </div>
        </div>
        <div className="info-item">
          <span className="info-icon">📆</span>
          <div>
            <h4>Monthly Limit</h4>
            <p>One push notification per month included with your advertiser plan (a rejected one does not count). To keep notifications welcome, we send at most 2 partner notifications per week across all advertisers — if this week is full, yours is sent the following week.</p>
          </div>
        </div>
        <div className="info-item">
          <span className="info-icon">✅</span>
          <div>
            <h4>Admin Approval</h4>
            <p>All notifications are reviewed before sending to ensure quality</p>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {/* Cannot Send Notice */}
      {!canSend && !pendingNotification && (
        <div className="cannot-send-notice">
          <div className="notice-icon">⏳</div>
          <div className="notice-text">
            <h3>Monthly Notification Used</h3>
            <p>You've already sent your push notification this month ({currentPeriod}).</p>
            <p>Next available in <strong>{getNextAvailableText()}</strong>.</p>
          </div>
        </div>
      )}

      {/* Pending Notification */}
      {pendingNotification && pendingNotification.status === 'pending' && (
        <div className="pending-notification">
          <div className="pending-header">
            <span className="pending-icon">⏳</span>
            <h3>Notification Pending Approval</h3>
          </div>
          <div className="pending-preview">
            <div className="preview-title">{pendingNotification.title}</div>
            <div className="preview-body">{pendingNotification.body}</div>
          </div>
          {pendingNotification.selectedContent?.length > 0 && (
            <div className="pending-content">
              <p className="pending-content-label">Includes content:</p>
              {pendingNotification.selectedContent.map((c, i) => (
                <span key={i} className="pending-content-tag">
                  {contentTypeEmojis[c.type] || '📄'} {c.title}
                </span>
              ))}
            </div>
          )}
          <p className="pending-note">Your notification is being reviewed. The result shows here: once approved it is sent; if it is not approved you see the reason and can send a new one.</p>
        </div>
      )}

      {/* Rejected push (this month): what was rejected and why; the month is not used up */}
      {lastRejected && canSend && !pendingNotification && (
        <div className="cannot-send-notice push-rejected" role="status">
          <div className="notice-icon">❌</div>
          <div className="notice-text">
            <h3>Your last notification was not approved</h3>
            <p><strong>{lastRejected.title}</strong> — {lastRejected.body}</p>
            <p><strong>Reason:</strong> {lastRejected.reason || 'No reason was given — please contact us if it is unclear.'}</p>
            {lastRejected.at && <p className="pending-note">Reviewed {lastRejected.at.toLocaleDateString()}.</p>}
            <p>This does not use up your monthly notification: change it and submit again.</p>
            <button type="button" className="submit-btn"
                    onClick={() => { setFormData({ title: lastRejected.title || '', body: lastRejected.body || '' });
                      setSelectedContentIds((lastRejected.selectedContent || []).map((c) => c.contentId).filter((id) => approvedContent.some((a) => a.id === id))); }}>
              ✏️ Edit the rejected text</button>
          </div>
        </div>
      )}

      {/* Form */}
      {canSend && !pendingNotification && (
        <div className="notification-form-container">
          <h3>Compose Your Notification</h3>

          {/* Content Selection */}
          {approvedContent.length > 0 && (
            <div className="content-selection">
              <label>Include approved content (optional)</label>
              <p className="selection-hint">Select content to feature in this push notification</p>
              <div className="content-checkboxes">
                {approvedContent.map(item => (
                  <label key={item.id} className={`content-check-item ${selectedContentIds.includes(item.id) ? 'checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedContentIds.includes(item.id)}
                      onChange={() => toggleContentSelection(item.id)}
                    />
                    <span className="cci-emoji">{contentTypeEmojis[item.type] || '📄'}</span>
                    <span className="cci-title">{item.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">
                Title
                <span className="char-count">{formData.title.length}/{LIMITS.PUSH_TITLE_MAX}</span>
              </label>
              <input
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Spring Whale Season is Here!"
                maxLength={LIMITS.PUSH_TITLE_MAX}
              />
            </div>

            <div className="form-group">
              <label htmlFor="body">
                Message
                <span className="char-count">{formData.body.length}/{LIMITS.PUSH_BODY_MAX}</span>
              </label>
              <textarea
                id="body"
                name="body"
                value={formData.body}
                onChange={handleInputChange}
                placeholder="e.g., Join us for incredible humpback whale sightings. Book now and get 15% off!"
                maxLength={LIMITS.PUSH_BODY_MAX}
                rows={3}
              />
            </div>

            {/* Preview */}
            <div className="notification-preview">
              <h4>Preview</h4>
              <div className="phone-mockup">
                <div className="notification-card">
                  <div className="app-icon">🐋</div>
                  <div className="notification-content">
                    <div className="notification-title">
                      {formData.title || 'Your Title Here'}
                    </div>
                    <div className="notification-body">
                      {formData.body || 'Your message will appear here...'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Submitting...' : '📤 Submit for Approval'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tips */}
      <div className="tips-section">
        <h3>📝 Tips for Effective Notifications</h3>
        <ul>
          <li><strong>Be specific:</strong> Mention the whale species or special offers</li>
          <li><strong>Create urgency:</strong> Limited time offers perform better</li>
          <li><strong>Keep it short:</strong> Users skim notifications quickly</li>
          <li><strong>Include content:</strong> Link to your Featured Media or Hot Deal for more engagement</li>
        </ul>
      </div>
    </div>
  );
};

export default PushNotificationForm;
